import styled from 'threads5/styles/styled';
import { animated } from '@react-spring/web';
import { Stack } from 'src/components-v2/Layout';

/**
 * Shared styled components for ACH Calculator and Mortgage Calculator.
 * Single source of truth for form, card, slider, toggle, and disclaimer styles.
 */

export const Form = styled('form')(({ theme }) => {
  return {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2.5),
    margin: '16px 0 0 0',
    '& label': {
      margin: '0',
      '& input': {
        margin: '0',
      },
    },
    [theme.breakpoints.up('sm')]: {
      margin: '20px 0 0 0',
    },
  };
});

export const Card = styled('div')(({ theme }) => {
  return {
    margin: '0',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    boxShadow: '0 4px 15px 0 rgba(7, 87, 141, 0.30)',
    borderRadius: '24px',
    padding: '40px',
    position: 'relative',
    background: theme.palette['base/white'],
    hr: {
      margin: '0',
      width: '100%',
      height: '1px',
      background: theme.palette['base/purple/200'],
      border: 'none',
    },
    maxWidth: '416px',
    [theme.breakpoints.down('md')]: {
      maxWidth: '100%',
    },
    'p, span': {
      color: theme.palette['web3/component/ach-calculator/card/text'],
    },
  };
});

export const Slider = styled(animated.input, {
  shouldForwardProp: (prop) => {
    return prop !== 'isPulsing';
  },
})<{ isPulsing?: boolean }>(({ theme, isPulsing = false }) => {
  return {
    appearance: 'none',
    width: '100%',
    height: '10px',
    borderRadius: 45,
    outline: 'none',
    padding: 0,
    margin: 0,
    position: 'relative',
    display: 'none',
    [theme.breakpoints.up('sm')]: {
      display: 'block',
    },

    // Default track (WebKit)
    '::-webkit-slider-runnable-track': {
      height: '10px',
      borderRadius: 45,
      background:
        'linear-gradient(90deg, #42F0CD 0%, #56DDF7 33.65%, #D8C2FB 65.38%, #E9F 100%)',
    },

    // unfilled track (default 100% = empty until resizeUnfilledSlider runs)
    '&::after': {
      content: '""',
      width: 'var(--progress, 100%)',
      height: '10px',
      position: 'absolute',
      right: 0,
      borderRadius: '0 45px 45px 0',
      background:
        theme.palette['web3/component/ach-calculator/slider-track-background'],
    },

    // pulsing thumb
    '&::before': {
      '@keyframes pulse': {
        '0%': {
          boxShadow: '0 0 0 0 rgba(154, 237, 254, 0.5)',
        },
        '70%': {
          boxShadow: '0 0 0 24px rgba(154, 237, 254, 0)',
        },
        '100%': {
          boxShadow: '0 0 0 0 rgba(154, 237, 254, 0)',
        },
      },
      animation: isPulsing ? 'pulse 2.5s infinite' : 'none',
      content: '""',
      position: 'absolute',
      display: 'block',
      width: 24,
      height: 24,
      right: 'calc(var(--progress, 100%))',
      transform: 'translateX(var(--progress, 100%))',
      zIndex: 1,
      marginTop: '-7px',
      borderRadius: '100%',
    },

    // Track in Firefox
    '::-moz-range-track': {
      height: 6,
      borderRadius: 45,
      background:
        'linear-gradient(90deg, #42F0CD 0%, #56DDF7 33.65%, #D8C2FB 65.38%, #E9F 100%)',
    },
    '::-moz-range-progress': {
      height: 6,
      borderRadius: 45,
      background:
        'linear-gradient(90deg, #42F0CD 0%, #56DDF7 33.65%, #D8C2FB 65.38%, #E9F 100%)',
    },

    // Thumb (WebKit)
    '::-webkit-slider-thumb': {
      appearance: 'none',
      width: 24,
      height: 24,
      borderRadius: '100%',
      background: 'white',
      marginTop: '-7px',
      boxShadow: '0px 0px 5px 2px rgba(0,0,0,0.33)',
      position: 'relative',
      transition:
        'box-shadow 0.3s ease-in-out, width 0.3s ease-in-out, height 0.3s ease-in-out, margin 0.3s ease-in-out',
      zIndex: 1,
      cursor: 'grab',

      '&:hover': {
        width: 26,
        height: 26,
        marginTop: '-8px',
        boxShadow: '0px 0px 6px 3px rgba(0,0,0,0.22)',
      },
    },

    // Thumb (Firefox)
    '::-moz-range-thumb': {
      width: 24,
      height: 24,
      borderRadius: '100%',
      background: 'white',
      boxShadow: '0px 0px 5px 2px rgba(0,0,0,0.33)',
      transition:
        'box-shadow 0.3s ease-in-out, width 0.3s ease-in-out, height 0.3s ease-in-out, margin 0.3s ease-in-out',
      cursor: 'grab',

      '&:hover': {
        width: 26,
        height: 26,
        marginTop: '-8px',
        boxShadow: '0px 0px 6px 3px rgba(0,0,0,0.22)',
      },
    },
  };
});

export const Label = styled('label')(({ theme }) => {
  return {
    color: theme.palette['web3/text/contrast-max'],
    fontFamily: theme.typography.cern,
    fontSize: theme.spacing(2.25),
    fontStyle: 'normal',
    fontWeight: '600',
    lineHeight: '150%',
    letterSpacing: '-0.09px',
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: 'auto',
    },

    // we style input this way because of f'ing global styles
    input: {
      border: `1px solid ${theme.palette['base/black/400']}`,
      borderRadius: '8px',
      color: theme.palette['base/black/1000'],
      fontFamily: theme.typography.cern,
      fontFeatureSettings: "'liga' off, 'clig' off",
      fontSize: theme.spacing(2.25),
      fontStyle: 'normal',
      fontWeight: '500',
      height: '40px',
      letterSpacing: '0.18px',
      lineHeight: '24px',
      padding: '8px 16px 8px 12px',
      '&:focus': {
        border: `1px solid ${theme.palette['base/sky/1000']}`,
        borderRadius: '8px',
        color: theme.palette['base/black/1000'],
      },
      // tablet and up
      [theme.breakpoints.up('sm')]: {
        fontSize: theme.spacing(2.5),
        letterSpacing: '0.2px',
        padding: '16px',
      },
    },
  };
});

export const Disclaimer = styled('small')(({ theme }) => {
  return {
    '& p': {
      color:
        theme.palette['web3/component/ach-calculator/card/disclaimer'] +
        ' !important',
      fontFamily: theme.typography.cern,
      fontSize: theme.spacing(1.75),
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: '150%',
      '& a': {
        textDecoration: 'underline',
      },
    },
  };
});

/**
 * Pill-shaped two-option toggle. The labels are absolutely positioned inside
 * the track so a knob can slide underneath them; the ACH calculator animates
 * that knob with react-spring, but the track and labels are independent of it.
 */
export const ToggleTrack = styled(Stack)(({ theme }) => {
  return {
    position: 'relative',
    height: '54px',
    background: theme.palette['base/black/200'],
    borderRadius: '40px',
    width: '100%',
    maxWidth: '250px',
    [theme.breakpoints.up('sm')]: {
      maxWidth: '426px',
    },
    padding: '3px',
    boxShadow: '0 2px 8px 0 rgba(0, 0, 0, 0.10) inset',
    margin: '0 auto',
  };
});

export const ToggleLabel = styled('label', {
  shouldForwardProp: (prop) => {
    return prop !== 'isChecked';
  },
})<{ isChecked?: boolean }>(({ theme, isChecked = false }) => {
  return {
    height: '48px',
    color: isChecked
      ? theme.palette['base/sky/900']
      : theme.palette['base/black/600'],
    width: 'calc(50% - 3px)',
    fontFamily: theme.typography.cern,
    fontSize: theme.spacing(2.125),
    fontStyle: 'normal',
    fontWeight: isChecked ? '600' : '400',
    lineHeight: '150%',
    letterSpacing: '0.085px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'absolute',
    zIndex: 1,
    cursor: 'pointer',
    userSelect: 'none',
    borderRadius: '40px',
    transition: 'font-weight 0.1s ease-in-out, color 0.1s ease-in-out',
    WebkitTapHighlightColor: 'transparent',
    '&:has(:focus-visible)': {
      outline: `2px solid ${theme.palette['base/sky/1000']}`,
      outlineOffset: '2px',
    },
    '&:last-of-type': {
      marginLeft: '3px',
    },
  };
});

export const ToggleInput = styled('input')(() => {
  return {
    opacity: 0,
    position: 'absolute',
  };
});

export const HelperText = styled('small')(({ theme }) => {
  return {
    color: theme.palette['web3/component/calculator/helper-text'],
    fontFamily: theme.typography.cern,
    fontSize: theme.spacing(1.75),
    fontStyle: 'italic',
    fontWeight: 300,
    lineHeight: '150%',
    letterSpacing: '-0.07px',
  };
});
